import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  ogType?: "website" | "article";
  jsonLd?: object | object[];
  breadcrumbs?: { name: string; url: string }[];
  noIndex?: boolean;
}

const SITE_URL = "https://growagenticcommerce.com";

const SEOHead = ({ title, description, canonical, ogImage, ogType = "website", jsonLd, breadcrumbs, noIndex }: SEOHeadProps) => {
  const fullCanonical = `${SITE_URL}${canonical}`;
  const fullOgImage = ogImage || `${SITE_URL}/og-growagentic.jpg`;

  const schemas: object[] = [];

  // Organization schema (always present)
  schemas.push({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "GrowAgentic",
    legalName: "Grow Agentic Commerce",
    url: SITE_URL,
    description: "Enterprise Answer Engine Optimization (AEO) and Agentic Commerce consulting platform — making commerce systems discoverable, trustable, and transactable by AI agents.",
    knowsAbout: [
      "Answer Engine Optimization",
      "Agentic Commerce",
      "AI Search Visibility",
      "Product Data Readiness",
      "Product Feed Optimization",
      "Ecommerce Transformation",
      "Conversational Commerce",
      "Multi-Agent Orchestration",
      "Agent-to-Agent Commerce",
      "Policy-as-Code",
      "Universal Commerce Protocol",
      "AI Agent Trust Frameworks",
      "Commerce API Modernization",
    ],
    sameAs: [
      "https://www.linkedin.com/company/agentic-commerce",
    ],
  });

  // WebSite schema (sitewide, enables sitelinks search treatment)
  schemas.push({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "GrowAgentic",
    url: SITE_URL,
    publisher: { "@type": "Organization", name: "GrowAgentic" },
    inLanguage: "en",
  });

  // WebPage schema with speakable
  schemas.push({
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: fullCanonical,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "[data-speakable]", ".section-container > p"],
    },
    publisher: {
      "@type": "Organization",
      name: "GrowAgentic",
    },
  });

  // Breadcrumb schema
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((crumb, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: crumb.name,
        item: `${SITE_URL}${crumb.url}`,
      })),
    });
  }

  // Additional JSON-LD (FAQPage, HowTo, etc.)
  if (jsonLd) {
    if (Array.isArray(jsonLd)) {
      schemas.push(...jsonLd);
    } else {
      schemas.push(jsonLd);
    }
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {noIndex && <meta name="googlebot" content="noindex, nofollow" />}
      <link rel="canonical" href={fullCanonical} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="GrowAgentic" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={title} />

      <script type="application/ld+json">
        {JSON.stringify(schemas)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
